'use client';

import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { EmptyState, Checkbox, CircularProgress } from 'src/components/PulseUI';
import { PulseHeader, PulseCell } from 'src/components/PulseUI/PulseTable';
import usePulseTableColumns from 'src/hooks/table/usePulseTableColumns';
import useStrategies from 'src/hooks/strategy/useStrategies';
import useStrategyMetricSort from 'src/hooks/strategy/useStrategyMetricSort';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import useSelectAll from 'src/hooks/strategy/useSelectAll';
import { LOCATION } from 'src/hooks/filters/constants';
import { useDebounce } from 'use-debounce';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLogging } from 'src/context/logging';
import { useSite } from 'src/context/providers/SiteProvider';
import { useGetTargetStrategyConnectorQuery, useGetMyChainedStrategiesQuery, useGetMappingsQuery } from 'src/api/redux';
import {
  useGetAllStrategiesMembersListInfiniteQuery,
  MembersResponse,
} from 'src/api/redux/strategy/allStrategiesApi';
import { useAppDispatch, setNavList, appendItems, clearNav } from 'src/redux';
import { useGetStrategyMetricsQuery } from 'src/api/redux/strategy';
import { AddToListModal } from 'src/components';
import CrmCell from 'src/components/Table/CrmCell';
import StrategyChainsCell from 'src/components/Table/StrategyChainsCell';
import {
  findSelectedChain,
  getTargetStrategyIdString,
  resolveEncountersDisplayOptions,
} from 'src/utils/strategyHelpers';
import { strategyListTableColumns } from 'src/components/Tables/components/Columns/ColumnDefinitions';
import { TableColumn } from 'src/components/Table';
import type { GridColDef } from '@mui/x-data-grid-premium';

interface StrategyListProps {
  search: string;
  showAddToListDialog: boolean;
  onCloseAddToList: () => void;
  savedColumnVisibility: Record<string, boolean>;
  onCountChange?: (count: { total: number; isFetching: boolean }) => void;
}

const StrategyList = ({
  search,
  showAddToListDialog,
  onCloseAddToList,
  savedColumnVisibility,
  onCountChange,
}: StrategyListProps) => {
  const log = useLogging();
  const { showEncountersTotals } = useFlags();
  const [debouncedSearch] = useDebounce(search, 500);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const {
    boundedTerritory,
    selectedTerritory,
    setSelectedProvider,
    selectedCities,
    selectedStates,
    selectedSpecialties,
    selectedSocTypes,
    selectedSocSubtypes,
    selectedStatuses,
    selectedProviderLists,
    selectedChainIds,
    metricToggles,
    setActiveMarker,
  } = useStrategies(LOCATION.STRATEGY_CENTER);

  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();
  const appDispatch = useAppDispatch();

  const { data: allMappings, isLoading: isMappingsLoading } =
    useGetMappingsQuery({
      sort: 'label',
      order: 'asc',
      page: 0,
      pageSize: 100,
    });

  const crmTypes = useMemo(() => {
    return Array.from(
      new Set(
        allMappings?.results
          ?.filter((mapping) => mapping.enabled)
          .map((mapping) => mapping.crm?.toLowerCase())
      )
    );
  }, [allMappings]);

  // Derive the selected chain and strategyId from chain data (selectedStrategy is never set in Strategy Center)
  const { data: chainedStrategies } = useGetMyChainedStrategiesQuery();
  const selectedChain = useMemo(
    () => findSelectedChain(selectedChainIds, chainedStrategies?.results),
    [selectedChainIds, chainedStrategies?.results]
  );
  const strategyId = getTargetStrategyIdString(selectedChain);

  const { currentData: strategyMetrics, isLoading: strategyMetricsLoading } =
    useGetStrategyMetricsQuery(
      {
        strategyType: 'target',
        strategyId,
      },
      {
        skip: !strategyId,
      }
    );

  // `metric_definitions` arrive with the members response, which itself depends on
  // the sort, so the default-sort fallback reads them one render behind.
  const [metricDefinitions, setMetricDefinitions] = useState<
    MembersResponse['metric_definitions'] | undefined
  >(undefined);

  const { sortValues, setSortValues, sortParams } = useStrategyMetricSort({
    selectedChainIds,
    strategyMetrics: strategyMetrics?.results,
    metricDefinitions,
    showEncountersTotals: !!showEncountersTotals,
    // The table sorts by the option its column displays, which is not filtered.
    defaultToSortableOption: false,
  });

  const queryParams = React.useMemo(
    () => ({
      chain_id: selectedChainIds,
      territory_id: selectedTerritory?.id,
      bounding_territory: boundedTerritory?.id,
      q: debouncedSearch || undefined,
      pageSize: 50,
      city: selectedCities,
      state: selectedStates,
      specialty: selectedSpecialties,
      soc_type: selectedSocTypes,
      soc_subtype: selectedSocSubtypes,
      crm_status: selectedStatuses,
      provider_list: selectedProviderLists,
      ...sortParams,
    }),
    [
      selectedChainIds,
      boundedTerritory?.id,
      selectedTerritory?.id,
      debouncedSearch,
      selectedCities,
      selectedStates,
      selectedSpecialties,
      selectedSocTypes,
      selectedSocSubtypes,
      selectedStatuses,
      selectedProviderLists,
      sortParams,
    ]
  );

  const skipQuery = selectedChainIds.length === 0;

  const {
    data: listMembersData,
    isLoading: listMembersLoading,
    isFetching: listMembersFetching,
    isFetchingNextPage,
    isError: listMembersError,
    error: listMembersErrorData,
    fetchNextPage,
  } = useGetAllStrategiesMembersListInfiniteQuery(queryParams, {
    skip: skipQuery,
  });

  const latestMetricDefinitions =
    listMembersData?.pages?.[0]?.metric_definitions;
  if (latestMetricDefinitions !== metricDefinitions) {
    setMetricDefinitions(latestMetricDefinitions);
  }

  // Flatten all pages, normalize rows, and deduplicate by provider_id
  // (offset-based pagination can return overlapping results across pages)
  const allResults = React.useMemo(() => {
    const seen = new Set<string>();
    return (listMembersData?.pages ?? []).flatMap((page) =>
      page.results.reduce<any[]>((acc, r) => {
        const pid = r.provider_id || r.id;
        if (!seen.has(pid)) {
          seen.add(pid);
          acc.push({ ...r, provider_id: pid });
        }
        return acc;
      }, [])
    );
  }, [listMembersData?.pages]);
  const totalCount = listMembersData?.pages?.[0]?.count ?? 0;

  useEffect(() => {
    onCountChange?.({ total: totalCount, isFetching: listMembersFetching });
  }, [totalCount, listMembersFetching, onCountChange]);

  const chainsMetadata = listMembersData?.pages?.[0]?.chains ?? {};
  const hasNextPage =
    !!listMembersData?.pages?.[listMembersData.pages.length - 1]?.next;

  const {
    effectiveProviders,
    isHeaderChecked,
    isResolvingSelection,
    effectiveCount,
    handleToggleSelectAll,
    resetSelection,
    showBanner,
    bannerState,
    handleSelectAllOpportunities,
    handleSelectFirstN,
    handleToggleProvider,
    loadedCount,
  } = useSelectAll(
    LOCATION.STRATEGY_CENTER,
    allResults,
    totalCount,
    queryParams,
    hasNextPage
  );

  const effectiveProviderSet = useMemo(
    () => new Set(effectiveProviders.map((p) => p.provider_id)),
    [effectiveProviders]
  );

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || listMembersFetching || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 200) {
      fetchNextPage();
    }
  }, [listMembersFetching, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Measure header height and set CSS variable for banner sticky offset
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const headerCell = grid.querySelector(
      '[role="columnheader"]'
    )?.parentElement;
    if (!headerCell) return;

    const update = () => {
      const gridTop = grid.getBoundingClientRect().top;
      const cellBottom = headerCell.getBoundingClientRect().bottom;
      grid.style.setProperty('--header-h', `${cellBottom - gridTop}px`);
    };
    const observer = new ResizeObserver(update);
    observer.observe(headerCell);
    update();
    return () => observer.disconnect();
  }, []);

  // Scroll to top when query params change (strategy/filter change resets the data)
  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [queryParams]);

  const listMembers = React.useMemo(
    () => ({
      results: allResults,
      count: totalCount,
    }),
    [allResults, totalCount]
  );

  // Keep nav slice in sync
  React.useEffect(() => {
    if (allResults.length > 0) {
      const navItems = allResults.map((r: any) => ({
        id: r.provider_id,
        type: r.type || null,
      }));
      appDispatch(appendItems(navItems));
    }
  }, [allResults.length, appDispatch]);

  // Clear nav and selection when query params change (filter/sort reset)
  const queryParamsRef = React.useRef(queryParams);
  React.useEffect(() => {
    if (queryParamsRef.current !== queryParams) {
      queryParamsRef.current = queryParams;
      appDispatch(clearNav());
      resetSelection();
    }
  }, [queryParams, appDispatch, resetSelection]);

  const { data: connector } = useGetTargetStrategyConnectorQuery(
    {
      strategyId,
      territoryId: selectedTerritory?.id.toString() || '',
    },
    {
      skip: !strategyId || !selectedTerritory?.id,
    }
  );
  // combine listMembers with connector?.last_refreshed (only if strategy auto-refreshes)
  const autoRefreshes =
    (selectedChain as any)?.target_strategy?.refresh_event !== 'NEVER' &&
    !!(selectedChain as any)?.target_strategy?.refresh_event;

  const combinedListMembers = useMemo(() => {
    if (!listMembers) return listMembers;

    return {
      ...listMembers,
      results: listMembers.results.map((member) => ({
        ...member,
        last_refreshed: autoRefreshes
          ? connector?.last_refreshed || null
          : undefined,
      })),
    };
  }, [listMembers, connector, autoRefreshes]);

  // Generate dynamic columns from the members response metric_definitions
  // Falls back to per-strategy metrics when metric_definitions is unavailable
  const dynamicColumns = React.useMemo(() => {
    const metricDefs = listMembersData?.pages?.[0]?.metric_definitions;

    if (metricDefs && Object.keys(metricDefs).length > 0) {
      // Build from all-strategies response
      // Match with per-strategy metric details for display_options when available
      const perStrategyMetricsMap = new Map<string, any>(
        (strategyMetrics?.results || []).map((m: any) => [m.id.toString(), m])
      );

      return Object.entries(metricDefs)
        .map(([metricId, def]: [string, any]) => {
          const perStrategyMetric = perStrategyMetricsMap.get(metricId);
          // Never render a standalone "Encounters" toggle; the encounters value is
          // surfaced under the claims toggle when the flag is on, relabeled to match.
          const availableOptions = resolveEncountersDisplayOptions<{
            code: string;
            label: string;
          }>(
            perStrategyMetric?.display_options?.map((option: any) => ({
              code: option.code,
              label: option.label,
            })),
            !!showEncountersTotals
          );

          return TableColumn?.strategyDynamicColumn(metricId, def.label, {
            metricId,
            availableOptions,
            displayOptions: perStrategyMetric?.display_options || [],
            executionParams: perStrategyMetric?.execution_params,
          });
        })
        .filter(Boolean);
    }

    // Fallback: per-strategy metrics
    if (!strategyMetrics?.results?.length) return [];

    return strategyMetrics.results
      .map((metric: any) => {
        const availableOptions = resolveEncountersDisplayOptions<{
          code: string;
          label: string;
        }>(
          metric.display_options?.map((option: any) => ({
            code: option.code,
            label: option.label,
          })),
          !!showEncountersTotals
        );

        return TableColumn?.strategyDynamicColumn(
          metric.id.toString(),
          metric.label,
          {
            metricId: metric.id.toString(),
            availableOptions,
            displayOptions: metric.display_options,
            executionParams: metric.execution_params,
          }
        );
      })
      .filter(Boolean);
  }, [listMembersData?.pages, strategyMetrics, showEncountersTotals]);

  // Map metric field → display_options for sorting (uses same source as columns)
  const metricOptionsMap = React.useMemo(() => {
    const map = new Map<string, Array<{ code: string; label: string }>>();
    const metricDefs = listMembersData?.pages?.[0]?.metric_definitions;
    const perStrategyMetricsMap = new Map<string, any>(
      (strategyMetrics?.results || []).map((m: any) => [m.id.toString(), m])
    );

    if (metricDefs && Object.keys(metricDefs).length > 0) {
      Object.entries(metricDefs).forEach(([metricId, def]: [string, any]) => {
        const perStrategyMetric = perStrategyMetricsMap.get(metricId);
        const options = resolveEncountersDisplayOptions<{
          code: string;
          label: string;
        }>(
          perStrategyMetric?.display_options?.map((opt: any) => ({
            code: opt.code,
            label: opt.label,
          })) ||
            def.display_options?.map((opt: any) => ({
              code: opt.code,
              label: opt.label,
            })),
          !!showEncountersTotals
        );
        map.set(metricId, options);
      });
    } else {
      (strategyMetrics?.results || []).forEach((metric: any) => {
        const options = resolveEncountersDisplayOptions<{
          code: string;
          label: string;
        }>(
          metric.display_options?.map((opt: any) => ({
            code: opt.code,
            label: opt.label,
          })),
          !!showEncountersTotals
        );
        map.set(metric.id.toString(), options);
      });
    }
    return map;
  }, [listMembersData?.pages, strategyMetrics, showEncountersTotals]);

  // Strategy column definition
  const strategyColumn: GridColDef = useMemo(
    () => ({
      field: 'chain_ids',
      headerName: 'Strategy',
      width: 300,
      minWidth: 200,
      sortable: false,
    }),
    []
  );

  // Combine static columns with dynamic columns from API
  // Hide volume columns when multiple strategies are selected (metrics don't align across strategies)
  const allColumns = React.useMemo(() => {
    const staticStart = strategyListTableColumns.slice(0, 6);
    const staticEnd = strategyListTableColumns.slice(6);
    const showVolumeColumns = selectedChainIds.length <= 1;

    return [
      staticStart[0],
      staticStart[1],
      strategyColumn,
      ...staticStart.slice(2),
      ...(showVolumeColumns ? dynamicColumns : []),
      ...staticEnd,
    ].filter(Boolean);
  }, [dynamicColumns, strategyColumn, selectedChainIds.length]);

  // Column visibility model - merge saved preferences with business logic
  const columnVisibilityModel = useMemo(
    () => ({
      ...savedColumnVisibility,
      name: true, // Always visible
      tags: savedColumnVisibility.tags ?? true,
      crm_links:
        crmTypes.length > 0 && savedColumnVisibility.crm_links !== false,
    }),
    [savedColumnVisibility, crmTypes.length]
  );

  // Filter columns by visibility model
  const visibleColumns = useMemo(
    () =>
      allColumns.filter((col) => columnVisibilityModel[col.field] !== false),
    [allColumns, columnVisibilityModel]
  );

  // PulseTable column defs for resize support
  const pulseColumns = useMemo(
    () => [
      { field: '__checkbox', headerName: '', width: 48, resizable: false },
      ...visibleColumns.map((col) => {
        return {
          field: col.field,
          headerName: col.headerName || col.field,
          width: col.width || (col.flex ? 200 : col.minWidth) || 150,
          minWidth: col.minWidth || 100,
          maxWidth: 600,
          resizable: true,
        };
      }),
    ],
    [visibleColumns]
  );

  const {
    startResizing: _startResizing,
    isResizing,
    resizingField,
    getGridTemplateColumns,
  } = usePulseTableColumns({ columns: pulseColumns });

  const wasResizingRef = useRef(false);

  useEffect(() => {
    if (isResizing) {
      wasResizingRef.current = true;
    } else if (wasResizingRef.current) {
      // Delay clearing so the click event from mouseup is suppressed
      const timer = setTimeout(() => {
        wasResizingRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isResizing]);

  const startResizing = useCallback(
    (field: string, e: React.MouseEvent) => {
      e.stopPropagation();
      _startResizing(field, e);
    },
    [_startResizing]
  );

  // Sort model - compute which field should show the sort indicator
  const sortModel = React.useMemo(() => {
    if (sortValues.sort === 'metric' && metricOptionsMap.size > 0) {
      // Find which metric column corresponds to the current sort_metric
      const entries = Array.from(metricOptionsMap.entries());
      for (let i = 0; i < entries.length; i++) {
        const [metricId, options] = entries[i];
        const firstOption = options[0]?.code;
        const selectedOption = (metricToggles || {})[metricId];
        const activeOption = options.find((opt) => opt.code === selectedOption)
          ? selectedOption
          : firstOption;
        if (activeOption === sortValues.sort_metric) {
          return [{ field: metricId, sort: sortValues.order }];
        }
      }
    } else if (sortValues.sort !== 'metric') {
      return [{ field: sortValues.sort, sort: sortValues.order }];
    }
    return [];
  }, [sortValues, metricOptionsMap, metricToggles]);

  const handleRowClick = (params: any) => {
    setSelectedProvider(params.row);
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: params.row.provider_id, type: params.row.type || null },
    });

    // Set up drawer navigation
    const navItems = allResults.map((r) => ({
      id: r.provider_id,
      type: r.type || null,
    }));
    const clickedIndex = navItems.findIndex(
      (item) => item.id === params.row.provider_id
    );
    appDispatch(
      setNavList({
        items: navItems,
        currentIndex: clickedIndex >= 0 ? clickedIndex : 0,
        totalCount,
        source: 'strategy-list',
      })
    );

    closeAllButCurrent('profile');

    log.event('Strategy list provider clicked', {
      source: 'StrategyList',
      providerId: params.row.provider_id,
      providerName: params.row.name,
    });
  };

  const handleAddToListSuccess = (result: {
    addedCount: number;
    targetListName: string;
  }) => {
    log.event('Providers added to list', {
      listName: result.targetListName,
      count: result.addedCount,
    });
    resetSelection();
  };

  const handleSortChange = (model) => {
    // If model is empty, keep the current sort (shouldn't happen with sortingOrder prop)
    if (!model?.length) {
      return;
    }

    const { field, sort: sortDirection } = model[0];
    const order: 'asc' | 'desc' = sortDirection || 'desc';

    // Check if field is a metric column
    const options = metricOptionsMap.get(field);

    if (options && options.length > 0) {
      const selectedDisplayOption = (metricToggles || {})[field];
      const validOption = options.find(
        (opt) => opt.code === selectedDisplayOption
      );
      const activeOption = validOption ? validOption.code : options[0].code;

      setSortValues({
        sort: 'metric',
        sort_metric: activeOption,
        order: order,
      });

      log.event('Strategy list sorted', {
        source: 'StrategyList',
        sortField: activeOption,
        sortOrder: order,
      });
    } else {
      // Use the field name for non-metric columns
      setSortValues({
        sort: field,
        sort_metric: null,
        order: order,
      });

      log.event('Strategy list sorted', {
        source: 'StrategyList',
        sortField: field,
        sortOrder: order,
      });
    }
  };

  const isLoading =
    listMembersLoading || strategyMetricsLoading || isMappingsLoading;
  // Determine if we should show empty state instead of data grid
  const showEmptyState =
    listMembersError ||
    (!isLoading && !skipQuery && listMembers?.results?.length === 0);
  return (
    <div
      data-testid="strategy-list"
      className="w-full h-full bg-white flex flex-col"
      role="region"
      aria-label="Strategy opportunities list"
    >
      <div className="flex-1 flex flex-col gap-3 min-h-0">
        {/* Content Area - show empty state or data grid */}
        <div
          data-testid="strategy-list-content"
          className="flex flex-col flex-1 min-h-0"
          role="region"
          aria-label="Opportunities results"
        >
          {listMembersError ? (
            <EmptyState
              title="Error Loading Opportunities"
              description={
                (listMembersErrorData as any)?.data?.error_message ||
                'Something went wrong. Please try again.'
              }
              icon={<Icon icon={faSearch} className="text-red-500 text-4xl" />}
              className="flex-1 border-none bg-transparent justify-center"
              size="lg"
            />
          ) : showEmptyState ? (
            <EmptyState
              title="No Results Found"
              description="No opportunities match your current filters. Try adjusting your search criteria."
              icon={
                <Icon icon={faSearch} className="text-slate-400 text-4xl" />
              }
              className="flex-1 border-none bg-transparent justify-center"
              size="lg"
            />
          ) : (
            <>
              <div className="relative flex-1 min-h-0 overflow-hidden">
                {isLoading && allResults.length > 0 && !isFetchingNextPage && (
                  <div
                    data-testid="strategy-list-refetch-overlay"
                    className="absolute inset-0 z-30 bg-white/60 flex items-center justify-center"
                  >
                    <CircularProgress size="sm" color="neutral" />
                  </div>
                )}
                <div
                  ref={scrollContainerRef}
                  className="h-full overflow-auto [container-type:inline-size] [scrollbar-gutter:stable]"
                >
                  {isLoading && allResults.length === 0 ? (
                    <div
                      data-testid="strategy-list-loading"
                      className="flex items-center justify-center h-full"
                    >
                      <CircularProgress />
                    </div>
                  ) : (
                    <>
                      <div
                        ref={gridRef}
                        data-testid="strategy-pulse-table-header"
                        role="grid"
                        className="grid w-full"
                        style={{
                          gridTemplateColumns: getGridTemplateColumns(),
                        }}
                      >
                        {/* Header row — sticky top, scrolls horizontally with data */}
                        <div className="sticky top-0 left-0 z-20 bg-neutral-50 border-b border-neutral-200 px-4 py-2 flex items-center">
                          <Checkbox
                            checked={isHeaderChecked}
                            onChange={handleToggleSelectAll}
                            size="sm"
                            aria-label="Select all providers"
                          />
                        </div>
                        {visibleColumns.map((col, colIndex) => {
                          const currentSort = sortModel[0];
                          const isSorted = currentSort?.field === col.field;
                          const isDynamic =
                            col.renderHeader && /^\d+$/.test(col.field);
                          const pulseCol = pulseColumns.find(
                            (p) => p.field === col.field
                          );
                          const colResizable = pulseCol?.resizable !== false;
                          const isNameCol = colIndex === 0;
                          return (
                            <div
                              key={col.field}
                              className={`sticky top-0 bg-neutral-50 border-b border-neutral-200 ${
                                isNameCol
                                  ? 'left-[48px] z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]'
                                  : 'z-10'
                              }`}
                              onClick={() => {
                                if (wasResizingRef.current) return;
                                if (col.sortable === false) return;
                                const newDir =
                                  isSorted && currentSort.sort === 'desc'
                                    ? 'asc'
                                    : 'desc';
                                handleSortChange([
                                  { field: col.field, sort: newDir },
                                ]);
                              }}
                            >
                              <PulseHeader
                                label={
                                  isDynamic
                                    ? col.renderHeader?.({ colDef: col } as any)
                                    : col.headerName
                                }
                                sortable={col.sortable !== false}
                                sortDirection={
                                  isSorted ? currentSort.sort : null
                                }
                                className="cursor-pointer"
                                resizable={colResizable}
                                onStartResize={
                                  colResizable
                                    ? (e) => startResizing(col.field, e)
                                    : undefined
                                }
                                isResizing={resizingField === col.field}
                              />
                            </div>
                          );
                        })}
                        {/* Filler header */}
                        <div className="sticky top-0 z-10 bg-neutral-50 border-b border-neutral-200" />
                      </div>

                      {/* Select All Banner — block element outside grid, sticky below header */}
                      {showBanner && (
                        <div
                          className="sticky left-0 z-10 w-[100cqw] pl-16 pr-4 py-2 bg-blue-50 border-b border-blue-200 text-sm text-secondary top-[var(--header-h,48px)]"
                          data-testid="select-all-banner"
                        >
                          {bannerState === 'page-selected' ? (
                            <>
                              {effectiveCount} of {totalCount} opportunities
                              selected.{' '}
                              {[50, 100, 250]
                                .filter(
                                  (n) =>
                                    n < (totalCount ?? 0) && n > effectiveCount
                                )
                                .map((n) => (
                                  <React.Fragment key={n}>
                                    <button
                                      onClick={() => handleSelectFirstN(n)}
                                      className="font-semibold text-sky-700 hover:text-sky-900 cursor-pointer"
                                      data-testid={`select-first-${n}-link`}
                                    >
                                      Select first {n}
                                    </button>
                                    {' · '}
                                  </React.Fragment>
                                ))}
                              <button
                                onClick={handleSelectAllOpportunities}
                                className="font-semibold text-sky-700 hover:text-sky-900 cursor-pointer"
                                data-testid="select-all-opportunities-link"
                              >
                                Select all {totalCount}
                              </button>
                            </>
                          ) : (
                            <>
                              All {totalCount} opportunities are selected.{' '}
                              <button
                                onClick={resetSelection}
                                className="font-semibold text-sky-700 hover:text-sky-900 cursor-pointer"
                                data-testid="clear-selection-link"
                              >
                                Clear selection
                              </button>
                            </>
                          )}
                        </div>
                      )}

                      {/* Data grid */}
                      <div
                        data-testid="strategy-pulse-table"
                        role="grid"
                        className="grid w-full"
                        style={{
                          gridTemplateColumns: getGridTemplateColumns(),
                        }}
                      >
                        {(combinedListMembers?.results || []).map((row) => {
                          const isSelected = effectiveProviderSet.has(
                            row.provider_id
                          );
                          const rowBg = isSelected
                            ? 'bg-neutral-50'
                            : 'bg-white';
                          return (
                            <React.Fragment key={row.provider_id}>
                              <div
                                className={`sticky left-0 z-[5] border-b border-neutral-100 px-4 py-2 flex items-center cursor-pointer transition-colors ${rowBg} ${
                                  isSelected ? '' : 'hover:bg-neutral-50'
                                }`}
                                onClick={() => handleRowClick({ row })}
                                onMouseEnter={() =>
                                  setActiveMarker(row.provider_id?.toString())
                                }
                                onMouseLeave={() => setActiveMarker(null)}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleProvider(row.provider_id, row);
                                  }}
                                  size="sm"
                                />
                              </div>
                              {visibleColumns.map((col, colIndex) => {
                                const isNameCol = colIndex === 0;
                                return (
                                  <PulseCell
                                    key={col.field}
                                    className={`border-b border-neutral-100 cursor-pointer transition-colors ${rowBg} ${
                                      isSelected ? '' : 'hover:bg-neutral-50'
                                    } ${isNameCol ? 'sticky left-[48px] z-[5] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''}`}
                                    onMouseEnter={() =>
                                      setActiveMarker(
                                        row.provider_id?.toString()
                                      )
                                    }
                                    onMouseLeave={() => setActiveMarker(null)}
                                  >
                                    <div
                                      className="w-full h-full flex items-center"
                                      onClick={() => handleRowClick({ row })}
                                      data-testid={
                                        col.field === 'name'
                                          ? `strategy-list-row-${row.provider_id}`
                                          : undefined
                                      }
                                    >
                                      {col.field === 'chain_ids' ? (
                                        <StrategyChainsCell
                                          chainIds={row.chain_ids || []}
                                          chainsMetadata={chainsMetadata}
                                        />
                                      ) : col.field === 'crm_links' ? (
                                        <CrmCell row={row} />
                                      ) : col.renderCell ? (
                                        col.renderCell({
                                          row,
                                          field: col.field,
                                          value: row[col.field],
                                        } as any)
                                      ) : (
                                        <span className="truncate">
                                          {typeof row[col.field] === 'object'
                                            ? '—'
                                            : (row[col.field] ?? '—')}
                                        </span>
                                      )}
                                    </div>
                                  </PulseCell>
                                );
                              })}
                              {/* Filler cell */}
                              <div
                                className={`border-b border-neutral-100 cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'bg-neutral-50'
                                    : 'hover:bg-neutral-50'
                                }`}
                                onClick={() => handleRowClick({ row })}
                              />
                            </React.Fragment>
                          );
                        })}
                      </div>
                      {isFetchingNextPage && (
                        <div className="flex items-center justify-center py-4">
                          <CircularProgress size="sm" color="neutral" />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <AddToListModal
        isOpen={showAddToListDialog}
        onClose={onCloseAddToList}
        mode="add"
        providers={effectiveProviders}
        isResolving={isResolvingSelection}
        onSuccess={handleAddToListSuccess}
      />
    </div>
  );
};

export default StrategyList;
